import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
} from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";
import { sectionRenderer } from "@theme/section-renderer";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);
    this.#initializeCollectionFilters();
    this.setAttribute("initialized", "");
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute("product-grid-view", value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout("default");
  };

  /**
   * Initialize collection filter functionality
   */
  #initializeCollectionFilters() {
    const collectionLinks = this.querySelectorAll(
      '.collection-filter-block a[href*="/collections/"]',
    );

    collectionLinks.forEach((link) => {
      link.addEventListener(
        "click",
        this.#handleCollectionFilterClick.bind(this),
      );
    });
  }

  /**
   * Handle collection filter click
   */
  #handleCollectionFilterClick = async (event) => {
    event.preventDefault();

    const link = event.currentTarget;
    const collectionUrl = new URL(link.href);
    const collectionHandle =
      link.getAttribute("data-handle") ||
      collectionUrl.pathname.split("/collections/")[1];

    if (!collectionHandle) {
      window.location.href = link.href;
      return;
    }

    try {
      await this.#loadCollectionContent(collectionHandle, collectionUrl);
    } catch (error) {
      console.error("Failed to load collection:", error);
      window.location.href = link.href;
    }
  };

  /**
   * Load collection content dynamically
   */
  #loadCollectionContent = async (collectionHandle, collectionUrl) => {
    const mainCollectionItems = document.getElementById(
      "main-collection-items",
    );
    if (!mainCollectionItems) {
      throw new Error("main-collection-items element not found");
    }

    this.#showLoader(mainCollectionItems);

    try {
      // Update browser URL
      window.history.pushState({}, "", collectionUrl.href);

      // Get section ID from the current results-list element
      const sectionId = this.getAttribute("section-id");
      if (!sectionId) {
        throw new Error("Section ID not found");
      }

      // Fetch new collection content
      const sectionHTML = await sectionRenderer.getSectionHTML(
        sectionId,
        false,
        collectionUrl,
      );

      // Parse the HTML to extract the main-collection-items content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, "text/html");
      const newMainCollectionItems = doc.getElementById(
        "main-collection-items",
      );

      if (!newMainCollectionItems) {
        throw new Error("New collection content not found");
      }

      // Replace the content with smooth transition
      await startViewTransition(() => {
        mainCollectionItems.innerHTML = newMainCollectionItems.innerHTML;
      });

      // Update active filter state
      this.#updateActiveFilterState(collectionHandle);
    } finally {
      this.#hideLoader(mainCollectionItems);
    }
  };

  /**
   * Show loader - hide content and show placeholder
   */
  #showLoader(container) {
    // Hide the current content
    const productGrid = container.querySelector('[id^="product-grid"]');
    if (productGrid) {
      productGrid.style.display = "none";
    }

    // Show the placeholder
    const placeholder = document.getElementById("placeholder-div");
    if (placeholder) {
      placeholder.style.display = "grid";
    }
  }

  /**
   * Hide loader - show content and hide placeholder
   */
  #hideLoader(container) {
    // Show the content
    const productGrid =
      container.querySelector('[id^="product-grid"]') ||
      container.querySelector(".product-grid");
    if (productGrid) {
      productGrid.style.display = "";
    }

    // Hide the placeholder
    const placeholder = document.getElementById("placeholder-div");
    if (placeholder) {
      placeholder.style.display = "none";
    }
  }

  /**
   * Update active filter state
   */
  #updateActiveFilterState(currentHandle) {
    const allFilterLinks = this.querySelectorAll(
      '.collection-filter-block a[href*="/collections/"]',
    );

    allFilterLinks.forEach((link) => {
      const linkUrl = new URL(link.href);
      const linkHandle = link.getAttribute("data-handle");
      const titleElement = link.querySelector("h6");

      if (titleElement) {
        if (linkHandle === currentHandle) {
          titleElement.classList.add("underline");
        } else {
          titleElement.classList.remove("underline");
        }
      }
    });
  }
}

/**
 * Toggle child collections visibility
 * @param {HTMLElement} element - The parent collection element that was clicked
 */
window.toggleChildCollections = function (element) {
  const childCollections = element.parentElement.nextElementSibling;
  const toggleIndicator = element.querySelector(".toggle-indicator");
  const mobileWrapper = document.querySelector(".wrapper_for_mobile");

  // Check if currently expanded by looking at toggle indicator
  const isExpanded = toggleIndicator && toggleIndicator.textContent === "(-)";

  if (!isExpanded) {
    // Show child collections
    if (window.innerWidth > 768) {
      // Desktop: show original child collections
      childCollections.classList.remove("hidden");
    } else if (mobileWrapper) {
      // Mobile: move to mobile wrapper
      const mobileChildCollections = childCollections.cloneNode(true);
      if (mobileChildCollections instanceof Element) {
        mobileChildCollections.classList.remove("ml-8", "hidden");
        mobileChildCollections.classList.add(
          "flex",
          "items-center",
          "gap-4",
          "overflow-x-auto",
          "pb-2",
          "whitespace-nowrap",
          "no-scrollbar",
        );

        // Clear any existing content in mobile wrapper
        mobileWrapper.innerHTML = "";
        mobileWrapper.appendChild(mobileChildCollections);

        // Add click event listeners to the mobile collection links
        const mobileCollectionLinks = mobileChildCollections.querySelectorAll(
          'a[href*="/collections/"]',
        );
        const originalLinks = childCollections.querySelectorAll(
          'a[href*="/collections/"]',
        );

        mobileCollectionLinks.forEach((link, index) => {
          link.addEventListener("click", (event) => {
            event.preventDefault();
            // Trigger click on the corresponding original link to use existing event handling
            if (originalLinks[index]) {
              originalLinks[index].click();
            }
          });
        });
      }
    }

    if (toggleIndicator) {
      toggleIndicator.textContent = "(-)";
    }
  } else {
    // Hide child collections
    if (window.innerWidth > 768) {
      // Desktop: hide original child collections
      childCollections.classList.add("hidden");
    }

    // Clear mobile wrapper
    if (mobileWrapper) {
      mobileWrapper.innerHTML = "";
    }

    if (toggleIndicator) {
      toggleIndicator.textContent = "(+)";
    }
  }
};

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
